import React, { useEffect, useState, useRef } from 'react';
import { Box, SimpleGrid, useToast, Flex, Button } from '@chakra-ui/react';
import DynamicTable from '../../../components/table/DynamicTable.js';
import { columnsDataStudyPlans } from './variables/columnsDataStudyPlans.js';
import { getData, postData, putData, deleteData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import CreateModal from '../../../components/studyPlan/CreateModal.js';
import ConfirmModal from '../../../components/modal/ConfirmModal.js';

export default function Settings() {
  const { searchQuery, updateSearchQuery } = useSearch();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalState, setModalState] = useState({
    isOpen: false,
    confirmModalOpen: false,
  });

  const [formData, setFormData] = useState({});
  const [modalMode, setModalMode] = useState('create'); // create | edit
  const [selected, setSelected] = useState(null);

  const rowsPerPage = 10;
  const toast = useToast();

  const latestRequestRef = useRef(0);

  const getStudyPlans = async (dato) => {
    const requestId = ++latestRequestRef.current;
    try {
      const response = await getData(
        `/studyPlans?page=${page + 1}&limit=${rowsPerPage}&data=${dato}`,
      );

      if (requestId !== latestRequestRef.current) return;
      console.log(response);
      setData(response?.payload?.studyPlans || []);
      setTotalPages(Math.ceil(response?.payload?.total / rowsPerPage));
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      toast({
        title: 'Error al obtener planes de estudio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const createPostData = (formData) => {
    return {
      description: formData.description,
      studyId: formData.studyTypeId,
      startDate: formData.startDate,
    };
  };

  const createStudyPlan = async () => {
    try {
      const newStudyPlan = createPostData(formData);
      const response = await postData('/studyPlans', newStudyPlan);
      if (response?.payload) {
        toast({
          title: 'Plan de estudio creado con éxito',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        closeModal();
      }
    } catch (error) {
      toast({
        title: 'Error al crear plan de estudio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getStudyPlans(searchQuery);
  }, [page, searchQuery]);

  const resetForm = () => {
    setFormData({});
  };

  const openModal = () => {
    setModalMode('create');
    setSelected(null);
    setModalState({
      isOpen: true,
      confirmModalOpen: false,
    });
  };

  const openEdit = (row) => {
    setModalMode('edit');
    setSelected(row);
    setFormData({
      description: row.description || '',
      studyId: row?.study?.id || '',
      startDate: row.startDate || '',
    });
    setModalState({ isOpen: true, confirmModalOpen: false });
  };

  const removeStudyPlan = async (row) => {
    if (!window.confirm('¿Eliminar (baja lógica) este plan de estudio?')) return;
    try {
      await deleteData(`/studyPlans/${row.id}`);
      toast({ title: 'Plan eliminado', status: 'success', duration: 3000, isClosable: true });
      await getStudyPlans('');
    } catch (e) {
      toast({ title: 'Error al eliminar', description: e?.message, status: 'error', duration: 4000, isClosable: true });
    }
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      confirmModalOpen: false,
    });
    resetForm();
  };

  const showConfirmModal = () => {
    setModalState({
      isOpen: true,
      confirmModalOpen: true,
    });
  };

  const hiddeConfirmModal = () => {
    setModalState({
      isOpen: true,
      confirmModalOpen: false,
    });
  };

  const handleConfirm = async () => {
    if (modalMode === 'create') {
      await createStudyPlan();
    } else {
      try {
        const payload = {
          description: formData.description,
          studyId: Number(formData.studyId),
          startDate: formData.startDate,
          enabled: true,
        };
        await putData(`/studyPlans/${selected?.id}`, payload);
        toast({ title: 'Plan actualizado', status: 'success', duration: 3000, isClosable: true });
        closeModal();
      } catch (e) {
        toast({ title: 'Error al actualizar', description: e?.message, status: 'error', duration: 4000, isClosable: true });
      }
    }
    await getStudyPlans('');
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify="flex-end" mb={4}>
        <Button colorScheme="blue" onClick={() => openModal()}>
          Nuevo Plan
        </Button>
      </Flex>
      <SimpleGrid
        mb="20px"
        columns={{ sm: 1 }}
        spacing={{ base: '20px', xl: '20px' }}
      >
        <DynamicTable
          columnsData={columnsDataStudyPlans}
          data={data}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          openModal={openModal}
          onEdit={openEdit}
          onDelete={removeStudyPlan}
        />
      </SimpleGrid>

      <CreateModal
        isOpen={modalState.isOpen}
        onClose={showConfirmModal}
        formData={formData}
        setFormData={setFormData}
        onConfirm={handleConfirm}
        title={modalMode === 'edit' ? 'Editar plan de estudio' : 'Agregar plan de estudio'}
      />

      <ConfirmModal
        isOpen={modalState.confirmModalOpen}
        onClose={hiddeConfirmModal}
        onConfirm={closeModal}
        header="Confirmación de Cancelación"
        message="¿Estás seguro de que deseas cancelar? Todos los datos no guardados se perderán."
      />
    </Box>
  );
}
